import { useEffect } from "react";
import { motion } from "framer-motion";
import { FandomQuiz } from "@/components/FandomQuiz";
import { CharacterGallery } from "@/components/CharacterGallery";
import { RevealLines } from "@/components/Reveal";
import { AD, AD_PAIRS } from "@/components/apothecaryData";
import { PairSets } from "@/components/PairSets";
import { AD_CHARM } from "@/components/charms";

const ACCENT = {
  text: "text-emerald-300", soft: "text-emerald-200", border: "border-emerald-200/25", bar: "bg-emerald-400",
  hover: "hover:border-emerald-300 hover:bg-emerald-900/30", glow: "rgba(16,185,129,0.18)",
  panel: "bg-[#062019]/85", answer: "border-emerald-200/25 bg-[#03110d]/60",
};

export default function ApothecaryPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="relative pt-28" data-testid="apothecary-page">
      <div className="fixed inset-0 -z-10 bg-[#04120f]" data-testid="apothecary-bg" aria-hidden="true"
        style={{ backgroundImage: "radial-gradient(60% 50% at 85% 10%, rgba(16,185,129,0.25), transparent 70%), radial-gradient(45% 40% at 10% 85%, rgba(234,179,8,0.16), transparent 70%), radial-gradient(40% 35% at 50% 50%, rgba(13,148,136,0.12), transparent 70%)" }} />
      <section className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-10 pb-6 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-emerald-200">The Apothecary Diaries · fan corner</p>
          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tight" data-testid="apothecary-title">
            <RevealLines lines={[<>Herbs, secrets</>, <em className="text-emerald-300">and beads.</em>]} />
          </h1>
          <p className="mt-6 max-w-lg text-purple-50/90 leading-relaxed">
            Step into the Rear Palace, find your match, then wear their colors. Every character's bracelet can be made by hand just for you.
          </p>
        </div>
        <motion.div initial={{ opacity: 0, y: 40, rotate: 0 }} animate={{ opacity: 1, y: 0, rotate: 3 }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative mx-auto w-full max-w-[340px] lg:max-w-[400px]">
          <div className="absolute -inset-6 rounded-[32px] bg-emerald-500/20 blur-3xl" aria-hidden="true" />
          <img src="/img/apothecary-cover.jpg" alt="The Apothecary Diaries poster with Maomao and Jinshi under a starry sky"
            data-testid="apothecary-cover"
            className="relative w-full h-auto rounded-[20px] border border-emerald-200/30 shadow-[0_30px_80px_-20px_rgba(16,185,129,0.5)]" />
        </motion.div>
      </section>
      <FandomQuiz id="ad-quiz" prefix="ad" label="Rear Palace · personality" lines={["Who are you in", "the Rear Palace?"]}
        intro="Answer seven questions to find your Apothecary Diaries match." quiz={AD} accent={ACCENT} charm={AD_CHARM}>
        <CharacterGallery characters={AD.CHARACTERS} prefix="ad-gallery" accentText="text-emerald-300" accentSoft="text-emerald-200/90"
          blurb="Every character from the quiz, in their own colors. Pick one and make it yours for $10."
          cardClass="border-emerald-200/20 bg-[#062019]/85 hover:border-emerald-200/50" charm={AD_CHARM} />
        <PairSets pairs={AD_PAIRS} characters={AD.CHARACTERS} fandom="Apothecary Diaries" prefix="ad-pairs" accent={ACCENT} charm={AD_CHARM} />
      </FandomQuiz>
    </main>
  );
}
